// NO DEPENDENCIES!

export enum FeatureFlagKey {
  FacilityProfileV2 = "facility-profile-v2",
  LeftNavV2 = "left-nav-v2",
  RcraFacilityProfile = "rcra-facility-profile",
  Sse = "sse",

  TasksModule = "tasks-module",
  DocumentAI = "document-ai",
  SuggestedTasks = "suggested-tasks",
  TaskCalendarICSIntegration = "task-calendar-ics-integration",

  TierIIModule = "tierii-module", // formerly "module-tierii"
  Products = "products", // formerly "countable-items-ui"
  Measurements = "measurements", // formerly "eager-beaver"
  FacilitySelfAssignment = "facility-self-assignment",
  InsightsUI = "insights-ui",
  Scout = "scout",
  ScoutReasoningText = "scout-reasoning-text",
  ScoutLoadTest = "scout-load-test",
  ScoutWriteAccess = "scout-write-access",

  WasteModule = "waste-module", // formerly "module-waste"
  WasteSubmissions = "waste-submissions", // formerly "submissions"
  WasteStreamsTenantPage = "waste-streams-tenant-page",
  WasteStateRegistration = "waste-state-registration",
  WasteManifestDiscrepancies = "waste-manifest-discrepancies",
  WasteEpaCorrectionDiscrepancies = "waste-epa-correction-discrepancies",
  WasteManifestCompliance = "waste-manifest-compliance",
  WasteExceptionReporting = "waste-exception-reporting",
  ManifestDocumentIngest = "manifest-document-ingest",
  GeppettoSelfHealing = "geppetto-self-healing",
}

export enum FeatureFlagType {
  Module = "MODULE",
  AddOn = "ADD_ON",
  Other = "OTHER",
}

export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

export type FeatureFlagDefinition = Readonly<{
  key: FeatureFlagKey;
  label: string;
  description: string;
  /**
   * Structural classification of the flag. Drives grouping in the
   * staff UI: MODULE flags render as top-level product sections, ADD_ON
   * flags render nested under their `parentKey` MODULE, and OTHER flags
   * collect anything that isn't product-module-shaped (staff/dev
   * toggles, UI experiments, etc).
   */
  type: FeatureFlagType;
  /**
   * Optional parent flag. Used to render ADD_ON flags under their
   * owning MODULE and to communicate "this requires X to work" without
   * enforcing a hard dependency graph.
   */
  parentKey: FeatureFlagKey | null;
  /**
   * Value returned when no TenantConfig row exists for this flag. Lets
   * us roll a flag out as on-by-default without seeding rows for every
   * tenant.
   */
  defaultValue: boolean;
  /**
   * When true, staff users see the flag as on regardless of tenant
   * configuration. Replaces the historical STAFF_OVERRIDE_FLAGS array.
   */
  staffOverride: boolean;
  /**
   * Orthogonal to `type`: marks a flag as still under active
   * development regardless of where it lives in the module hierarchy.
   * Surfaced as an "In development" chip in the staff UI. A top-level
   * flag can be `type: Other, inDevelopment: true`, and an add-on can
   * stay nested under its module with `inDevelopment: true`.
   */
  inDevelopment: boolean;
  /**
   * Additional flags that must be on for this flag to be meaningful,
   * beyond the implicit `parentKey` dependency. Use for sibling/cross-
   * module dependencies (e.g. `waste-manifest-compliance` requires
   * `waste-manifest-discrepancies` because the compliance compute path
   * piggy-backs on the discrepancy compute path). Display-only today —
   * not enforced by read or write paths. Must not contain the flag's
   * own key or its `parentKey`.
   */
  requires: readonly FeatureFlagKey[];
}>;

export type FeatureFlagMeta = Omit<FeatureFlagDefinition, "key">;

// Single source of truth — missing keys cause a type error via `satisfies`.
export const FEATURE_FLAG_META_BY_KEY = {
  [FeatureFlagKey.LeftNavV2]: {
    label: "Left Navigation & Landing Page V2",
    description:
      "Enable the redesigned grouped/collapsible left navigation and related route wrapper behavior, as well as the new landing page.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: true,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.FacilityProfileV2]: {
    label: "Facility Profile V2",
    description: "Enable the readonly facility profile V2 with modal-based editing.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: true,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.RcraFacilityProfile]: {
    label: "RCRA Facility Profile",
    description:
      "Enable the RCRA Facility Profile section on Facility Profile V2 (8700-12 site identification fields).",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: true,
    requires: [FeatureFlagKey.FacilityProfileV2],
  },
  [FeatureFlagKey.Sse]: {
    label: "SSE",
    description: "Enable the tenant SSE diagnostic page for staff users.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.TasksModule]: {
    label: "Tasks module",
    description: "Enable the Tasks module for this organization.",
    type: FeatureFlagType.Module,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.DocumentAI]: {
    // The internal/customer-facing name for this capability is now
    // "Document Intelligence". The flag key, enum identifier, GraphQL
    // types, and AWS infrastructure (lambda, ECR repo, ECS task) still
    // use "Document AI" / "document-ai" for now — those carry stored
    // state (TenantConfig rows, AWS resource names) and would need a
    // proper migration. This label/description rebrand is the
    // user-visible part.
    label: "Document Intelligence",
    description: "Enable document enrichment and parsing via Document Intelligence.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.SuggestedTasks]: {
    label: "Suggested Tasks",
    description: "Enable AI-powered task suggestions based on uploaded documents.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.TasksModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.TaskCalendarICSIntegration]: {
    label: "Task Calendar ICS Integration",
    description:
      "Enable ICS calendar subscription feeds for tasks. Users can subscribe to their tasks in external calendar apps.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.TasksModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.TierIIModule]: {
    label: "Tier II module",
    description: "Enable the Tier II reporting module for this organization.",
    type: FeatureFlagType.Module,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.Products]: {
    label: "Products",
    description: "Enable products (combinations of chemicals into countable items like batteries).",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.TierIIModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.Measurements]: {
    label: "Measurements",
    description: "Use measurements (on specific dates) for tracking chemical inventory.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.TierIIModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.FacilitySelfAssignment]: {
    label: "Facility self-assignment",
    description: "Allow users to assign themselves to facilities without admin approval.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.TierIIModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.InsightsUI]: {
    label: "Insights",
    description: "Enable the Insights UI.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.WasteModule]: {
    label: "Waste module",
    description: "Enable the Waste module for this organization.",
    type: FeatureFlagType.Module,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.WasteSubmissions]: {
    label: "Waste submissions",
    description: "Enable waste report submissions.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: false,
    requires: [],
  },
  [FeatureFlagKey.WasteStreamsTenantPage]: {
    label: "Tenant waste streams page",
    description: "Show the tenant-wide Waste Streams page and nav entry.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.WasteStateRegistration]: {
    label: "Waste state registration",
    description:
      "Surface state waste-stream registration fields and the related filters/columns for facilities in states that require registration.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.WasteManifestDiscrepancies]: {
    label: "Waste manifest discrepancies",
    description:
      "Enable field-level discrepancy computation between eManifest and vendor manifests.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.WasteEpaCorrectionDiscrepancies]: {
    label: "Waste EPA correction discrepancies",
    description: "Surface EPA correction requests from RCRAInfo as manifest discrepancies.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.WasteManifestDiscrepancies],
  },
  [FeatureFlagKey.WasteManifestCompliance]: {
    label: "Waste manifest compliance",
    description:
      "Enable e-Manifest upload compliance tracking (45/60 day thresholds). Requires WasteManifestDiscrepancies.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.WasteManifestDiscrepancies],
  },
  [FeatureFlagKey.WasteExceptionReporting]: {
    label: "Waste exception reporting",
    description:
      "Enable automatic creation of exception report submissions when a manifest reaches the 60-day threshold. Requires WasteManifestCompliance.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.WasteManifestCompliance],
  },
  [FeatureFlagKey.ManifestDocumentIngest]: {
    label: "Manifest document ingest",
    description: "Enable uploading manifest PDFs for AI extraction into waste manifest records.",
    type: FeatureFlagType.AddOn,
    parentKey: FeatureFlagKey.WasteModule,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    // No longer depends on DocumentAI: the document pipeline's where-clause
    // (`buildDocumentWhereClause`) admits manifest-only tenants via an
    // independent OR branch (ManifestDocumentIngest=true AND WasteModule=true,
    // limited to RCRAManifest docs). See
    // packages/encamp-ai/src/persistence/manifest/persistence.ts
    // (`canProcessManifests`) for the runtime gate.
    requires: [],
  },
  [FeatureFlagKey.GeppettoSelfHealing]: {
    label: "Geppetto self-healing",
    description:
      "Enable automatic LLM diagnosis and self-healing fix loop for failed Geppetto portal automation jobs.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.Scout]: {
    label: "Scout",
    description: "Enable the Scout workspace for this organization.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: true,
    inDevelopment: true,
    requires: [],
  },
  [FeatureFlagKey.ScoutReasoningText]: {
    label: "Scout reasoning text",
    description: "Show provider reasoning/thinking summaries in Scout conversations.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.Scout],
  },
  [FeatureFlagKey.ScoutLoadTest]: {
    label: "Scout load test",
    description:
      "Use the in-memory fake Scout provider for this organization's Scout conversations during load testing.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.Scout],
  },
  [FeatureFlagKey.ScoutWriteAccess]: {
    label: "Scout write access",
    description:
      "Enable the per-conversation toggle that lets Scout make data changes for this organization. Staff override is intentionally OFF — Scout write access must be explicitly opted in per tenant, including for internal testing. Until audit attribution, operational instructions, undo, and approval cards ship, no one should have ambient access to mutations in production.",
    type: FeatureFlagType.Other,
    parentKey: null,
    defaultValue: false,
    staffOverride: false,
    inDevelopment: true,
    requires: [FeatureFlagKey.Scout],
  },
} as const satisfies Readonly<Record<FeatureFlagKey, FeatureFlagMeta>>;

export const KNOWN_FEATURE_FLAG_KEYS = Object.keys(
  FEATURE_FLAG_META_BY_KEY
) as readonly FeatureFlagKey[];
// ^ the only as-cast we should need (maybe someday typescript will figure this out)

/**
 * Typed accessor for a flag's metadata. Prefer this over indexing
 * `FEATURE_FLAG_META_BY_KEY` directly: the underlying const uses
 * `as const satisfies …` to narrow literal values for the `defaults` /
 * `requires.length === 0` integrity tests, which means
 * `FEATURE_FLAG_META_BY_KEY[someFeatureFlagKey].requires` is the union
 * of every entry's literal tuple type. Calling `.includes(otherKey)`
 * on that union resolves the contravariant `searchElement` parameter
 * to `never`, breaking type-checking. Returning the wider
 * {@link FeatureFlagMeta} interface here avoids that pitfall while
 * keeping the const itself authoritative for everything else.
 */
export function getFeatureFlagMeta(key: FeatureFlagKey): FeatureFlagMeta {
  return FEATURE_FLAG_META_BY_KEY[key];
}

/**
 * Staff override flags are flags that are enabled by default for staff users.
 * Derived from FEATURE_FLAG_META_BY_KEY; declare `staffOverride: true` on a
 * flag's metadata rather than editing this list.
 */
export const STAFF_OVERRIDE_FLAGS: readonly FeatureFlagKey[] = KNOWN_FEATURE_FLAG_KEYS.filter(
  (key) => FEATURE_FLAG_META_BY_KEY[key].staffOverride
);

export function isFeatureFlagKey(key: string): key is FeatureFlagKey {
  return KNOWN_FEATURE_FLAG_KEYS.includes(key as FeatureFlagKey);
}

export const FEATURE_FLAG_DEFS: readonly FeatureFlagDefinition[] = KNOWN_FEATURE_FLAG_KEYS.map(
  (key) => ({
    key,
    ...FEATURE_FLAG_META_BY_KEY[key],
  })
);

export function getFeatureFlagDefaultValue(flag: FeatureFlagKey): boolean {
  return FEATURE_FLAG_META_BY_KEY[flag].defaultValue;
}

export function deriveFeatureFlagMapFromArray(
  keyValArray: { key: string; value: boolean }[]
): FeatureFlagMap {
  const valByKey = new Map(keyValArray.map(({ key, value }) => [key, value]));
  const flagMap = KNOWN_FEATURE_FLAG_KEYS.reduce<Partial<FeatureFlagMap>>((acc, key) => {
    acc[key] = valByKey.get(key) ?? FEATURE_FLAG_META_BY_KEY[key].defaultValue;
    return acc;
  }, {});
  // as-cast here only from Partial<FeatureFlagMap> to FeatureFlagMap
  // because we know all keys are present since we just iterated over them
  return flagMap as FeatureFlagMap;
}
